import { Badge, Button, Modal } from '@/components/UI';
import type { AuditPack, OptionTimingEvidenceReadiness } from './types';
import {
    badgeVariantForAuditGrade,
    formatMarketCap,
    formatPct01Number,
    formatSignedPct,
    labelForAuditProfile,
    optionTimingEvidenceBadgeState
} from './helpers';

interface RobustAuditModalProps {
    isOpen: boolean;
    activeAuditPack: AuditPack | null;
    activeExternalPrompt: string;
    externalAuditSessionPending: boolean;
    optionTimingEvidenceReadiness: OptionTimingEvidenceReadiness | null;
    optionTimingEvidenceLoading: boolean;
    selectedJobUsesOptionMetric: boolean;
    onStartExternalAuditSession: () => void;
    onCopyText: (text: string, label: string) => void;
    onClose: () => void;
}

const RobustAuditModal = ({
    isOpen,
    activeAuditPack,
    activeExternalPrompt,
    externalAuditSessionPending,
    optionTimingEvidenceReadiness,
    optionTimingEvidenceLoading,
    selectedJobUsesOptionMetric,
    onStartExternalAuditSession,
    onCopyText,
    onClose
}: RobustAuditModalProps) => {
    const timingEvidenceBadge = optionTimingEvidenceBadgeState(optionTimingEvidenceReadiness, optionTimingEvidenceLoading);

    return (
        <Modal
            isOpen={isOpen}
            size="xl"
            title={activeAuditPack ? `Robust Audit (Job #${activeAuditPack.job.id})` : 'Robust Audit'}
            onClose={onClose}
        >
            {!activeAuditPack ? (
                <div className="text-zinc-400 text-sm">
                    Run an audit on a completed optimizer job to score candidate robustness and rule coherence.
                </div>
            ) : (
                <>
                    <div className="text-zinc-400 text-xs mb-3">
                        Audit profile: <span className="text-white">{labelForAuditProfile(activeAuditPack.profile)}</span> · audited{' '}
                        <span className="text-white">{activeAuditPack.summary.candidatesAudited}</span> candidates · duplicate signatures{' '}
                        <span className="text-white">{activeAuditPack.summary.duplicateSignatureCount}</span>. Scores are capped when
                        holdout trades/entities are thin.
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-xs text-zinc-400">
                        <div>
                            Best result: <span className="text-white">{activeAuditPack.summary.bestResultId ?? '-'}</span>
                        </div>
                        <div>
                            Best score: <span className="text-white">{activeAuditPack.summary.bestScore ?? '-'}</span>
                        </div>
                        <div>
                            Best grade: <span className="text-white">{activeAuditPack.summary.bestGrade ?? '-'}</span>
                        </div>
                        <div>
                            Strong+: <span className="text-white">{activeAuditPack.summary.robustCandidateCount}</span>
                        </div>
                        <div>
                            Rejected: <span className="text-white">{activeAuditPack.summary.rejectedCandidateCount}</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        {selectedJobUsesOptionMetric && (
                            <Badge title={timingEvidenceBadge.message} variant={timingEvidenceBadge.variant}>
                                {timingEvidenceBadge.label}
                            </Badge>
                        )}
                        <Button disabled={externalAuditSessionPending} size="sm" variant="primary" onClick={onStartExternalAuditSession}>
                            {externalAuditSessionPending ? 'Creating...' : 'Generate Recommendation'}
                        </Button>
                        {activeExternalPrompt && (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => onCopyText(activeExternalPrompt, 'AI screener recommendation prompt')}
                            >
                                Copy AI Prompt
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onCopyText(activeAuditPack.externalWorkflowPrompts.codex, 'Codex prompt')}
                        >
                            Copy Legacy Codex Prompt
                        </Button>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onCopyText(activeAuditPack.externalWorkflowPrompts.claudeCode, 'Claude command')}
                        >
                            Copy Legacy Claude Command
                        </Button>
                    </div>
                    {selectedJobUsesOptionMetric && (
                        <div className="mb-3 rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs leading-5 text-zinc-500">
                            {timingEvidenceBadge.message}
                        </div>
                    )}

                    <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
                        {activeAuditPack.candidates.map((candidate) => (
                            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3" key={candidate.resultId}>
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <Badge variant={badgeVariantForAuditGrade(candidate.robustness.grade)}>
                                                {candidate.robustness.score} {candidate.robustness.grade}
                                            </Badge>
                                            <Badge variant="secondary">raw #{candidate.rawRank}</Badge>
                                            <Badge variant="secondary">id={candidate.resultId}</Badge>
                                        </div>
                                        <div className="text-white text-xs break-words">{candidate.ruleSummary}</div>
                                    </div>
                                    <div className="text-zinc-500 text-[11px] lg:text-right">
                                        stat {candidate.robustness.components.statistical} · universe{' '}
                                        {candidate.robustness.components.universe} · coherence{' '}
                                        {candidate.robustness.components.ruleCoherence}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 text-xs text-zinc-400">
                                    <div>
                                        HO trades:{' '}
                                        <span className="text-white">{candidate.metrics.holdoutTradeCount?.toLocaleString() ?? '-'}</span>
                                    </div>
                                    <div>
                                        HO entities: <span className="text-white">{candidate.entitySelection.holdoutUniqueEntities}</span>
                                    </div>
                                    <div>
                                        HO lift:{' '}
                                        <span className={(candidate.metrics.holdoutLift ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}>
                                            {candidate.metrics.holdoutLift !== null
                                                ? formatSignedPct(candidate.metrics.holdoutLift, 2)
                                                : '-'}
                                        </span>
                                    </div>
                                    <div>
                                        Large cap:{' '}
                                        <span className="text-white">
                                            {formatPct01Number(candidate.entitySelection.largeCapPassShare, 1)}
                                        </span>
                                    </div>
                                    <div>
                                        Optionable:{' '}
                                        <span className="text-white">
                                            {formatPct01Number(candidate.entitySelection.optionablePassShare, 1)}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-2 text-xs text-zinc-400">
                                    Top entities:{' '}
                                    {candidate.entitySelection.topEntities.slice(0, 8).map((entity, idx) => (
                                        <span className="mr-2" key={`${candidate.resultId}-${entity.entityId}`}>
                                            <span className="text-white">{entity.symbol ?? entity.entityId}</span>{' '}
                                            <span className="text-zinc-500">
                                                {entity.passCount} / {formatMarketCap(entity.marketCap)}
                                                {idx < Math.min(candidate.entitySelection.topEntities.length, 8) - 1 ? ',' : ''}
                                            </span>
                                        </span>
                                    ))}
                                </div>

                                {candidate.robustness.flags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {candidate.robustness.flags.slice(0, 6).map((flag) => (
                                            <Badge
                                                key={`${candidate.resultId}-${flag.code}`}
                                                variant={
                                                    flag.severity === 'danger'
                                                        ? 'danger'
                                                        : flag.severity === 'warning'
                                                          ? 'warning'
                                                          : 'secondary'
                                                }
                                            >
                                                {flag.code}
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                {(candidate.strategyFit.entryLogic.overlapFeatureKeys.length > 0 ||
                                    candidate.strategyFit.activeScreener.overlapFeatureKeys.length > 0) && (
                                    <div className="mt-2 flex flex-col gap-1">
                                        {candidate.strategyFit.entryLogic.overlapFeatureKeys.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge variant={candidate.strategyFit.entryLogic.fullyContained ? 'danger' : 'warning'}>
                                                    entry-logic overlap{' '}
                                                    {formatPct01Number(candidate.strategyFit.entryLogic.overlapFraction, 0)}
                                                </Badge>
                                                <span className="text-zinc-500 text-[11px]">
                                                    shares {candidate.strategyFit.entryLogic.overlapFeatureKeys.join(', ')} with the entry
                                                    logic
                                                </span>
                                            </div>
                                        )}
                                        {candidate.strategyFit.activeScreener.overlapFeatureKeys.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge variant={candidate.strategyFit.activeScreener.fullyContained ? 'danger' : 'warning'}>
                                                    screener overlap{' '}
                                                    {formatPct01Number(candidate.strategyFit.activeScreener.overlapFraction, 0)}
                                                </Badge>
                                                <span className="text-zinc-500 text-[11px]">
                                                    shares {candidate.strategyFit.activeScreener.overlapFeatureKeys.join(', ')} with the
                                                    active screener
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Modal>
    );
};

export default RobustAuditModal;
